import React, { useEffect, useRef, useState } from 'react';
import { Mic, Volume2, ChevronDown, Check, RefreshCw } from 'lucide-react';
import { useLang } from './i18n';
import { inputVolume, outputVolume, setInputVolume, setOutputVolume } from './audio-volume';

function VolumeSlider({ label, value, max, onChange }: { label: string; value: number; max: number; onChange: (v: number) => void }) {
  return <label className="voice-volume">
    <span className="voice-device-label"><span>{label}</span><b>{value}%</b></span>
    <input type="range" min={0} max={max} step={1} value={value} onChange={e => onChange(Number(e.target.value))} style={{ '--p': value / max } as React.CSSProperties} />
  </label>;
}

function CustomDeviceSelect({
  label,
  icon,
  storageKey,
  options,
  defaultLabel
}: {
  label: string;
  icon: React.ReactNode;
  storageKey: string;
  options: { id: string; label: string }[];
  defaultLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => localStorage.getItem(storageKey) || 'default');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);

  const allItems = [{ id: 'default', label: defaultLabel }, ...options];
  const current = allItems.find(x => x.id === selected) || allItems[0];

  return (
    <div className="voice-device-group" ref={wrapRef}>
      <label className="voice-device-label">
        {icon}
        <span>{label}</span>
      </label>
      <button
        type="button"
        className={'custom-select-trigger ' + (open ? 'is-open' : '')}
        onClick={() => setOpen(!open)}
      >
        <span className="custom-select-value">{current.label}</span>
        <ChevronDown size={16} className="custom-select-chevron" />
      </button>
      {open && (
        <div className="custom-select-menu" role="listbox">
          {allItems.map(item => {
            const active = item.id === selected;
            return (
              <button
                key={item.id}
                type="button"
                role="option"
                aria-selected={active}
                className={'custom-select-option ' + (active ? 'is-selected' : '')}
                onClick={() => {
                  setSelected(item.id);
                  localStorage.setItem(storageKey, item.id);
                  setOpen(false);
                }}
              >
                <span>{item.label}</span>
                {active && <Check size={15} className="custom-select-check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AudioSettings() {
  const [, , tr] = useLang();
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [error, setError] = useState('');
  const [noise,setNoise]=useState(()=>localStorage.getItem('malvo-noise-suppression')!=='0'),[noiseThreshold,setNoiseThreshold]=useState(()=>Number(localStorage.getItem('malvo-noise-threshold')||-45)),[mutedHint,setMutedHint]=useState(()=>localStorage.getItem('malvo-muted-hint')!=='0');
  const [detecting, setDetecting] = useState(false);
  const [inVol, setInVol] = useState(inputVolume), [outVol, setOutVol] = useState(outputVolume);

  async function load() {
    setDetecting(true);
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setDevices(await navigator.mediaDevices.enumerateDevices());
    } catch {
      setError(
        tr(
          'Please allow microphone access in your browser or app settings.',
          'Bitte erlaube den Mikrofonzugriff in deinem Browser.'
        )
      );
    } finally {
      setDetecting(false);
    }
  }

  useEffect(() => {
    if (navigator.mediaDevices?.enumerateDevices) {
      navigator.mediaDevices
        .enumerateDevices()
        .then(list => setDevices(list.filter(d => d.label)))
        .catch(() => {});
    }
  }, []);

  const inputs = devices
    .filter(d => d.kind === 'audioinput' && d.deviceId !== 'default')
    .map(d => ({ id: d.deviceId, label: d.label || `Microphone (${d.deviceId.slice(0, 6)})` }));

  const outputs = devices
    .filter(d => d.kind === 'audiooutput' && d.deviceId !== 'default')
    .map(d => ({ id: d.deviceId, label: d.label || `Speaker (${d.deviceId.slice(0, 6)})` }));

  return (
    <div className="voice-settings-container">
      <div className="voice-settings-top">
        <div>
          <h2>{tr('Voice & Video', 'Sprache & Video')}</h2>
          <p>{tr('Audio devices are saved locally on this device.', 'Geräte werden auf diesem Gerät gespeichert.')}</p>
        </div>
        <button type="button" className="voice-detect-btn" onClick={load} disabled={detecting}>
          <RefreshCw size={15} className={detecting ? 'spin' : ''} />
          {tr('Detect Audio Devices', 'Audiogeräte erkennen')}
        </button>
      </div>

      <div className="voice-devices-grid">
        <CustomDeviceSelect
          label={tr('Input Device (Microphone)', 'Eingabegerät (Mikrofon)')}
          icon={<Mic size={16} />}
          storageKey="malvo-audioinput"
          options={inputs}
          defaultLabel={tr('System Default Microphone', 'Systemstandard (Mikrofon)')}
        />

        <CustomDeviceSelect
          label={tr('Output Device (Speaker / Headset)', 'Ausgabegerät (Kopfhörer / Lautsprecher)')}
          icon={<Volume2 size={16} />}
          storageKey="malvo-audiooutput"
          options={outputs}
          defaultLabel={tr('System Default Output', 'Systemstandard (Ausgabe)')}
        />
      </div>

      <div className="voice-devices-grid voice-volume-grid">
        <VolumeSlider label={tr('Input Volume', 'Eingangslautstärke')} value={inVol} max={200} onChange={v => { setInVol(v); setInputVolume(v); }} />
        <VolumeSlider label={tr('Output Volume', 'Ausgangslautstärke')} value={outVol} max={100} onChange={v => { setOutVol(v); setOutputVolume(v); }} />
      </div>


      <div className="microphone-processing-settings">
        <label><input type="checkbox" checked={noise} onChange={e=>{setNoise(e.target.checked);localStorage.setItem('malvo-noise-suppression',e.target.checked?'1':'0');window.dispatchEvent(new Event('malvo-audio-processing'))}}/><span><b>{tr('Noise suppression','Rauschunterdrückung')}</b><small>{tr('Combines browser processing with a silence gate. Echo cancellation stays enabled.','Verbindet die Mikrofonverarbeitung deines Browsers mit einer Rauschsperre für Sprechpausen. Echounterdrückung bleibt aktiv.')}</small></span></label>
        {noise&&<label className="noise-threshold"><span><b>{tr('Background noise threshold','Grenze für Hintergrundrauschen')}</b><small>{tr('Raise if noise is still audible; lower if quiet words are cut off.','Erhöhen, wenn noch Rauschen hörbar ist; senken, wenn leise Wörter abgeschnitten werden.')}</small></span><input aria-label={tr('Background noise threshold','Grenze für Hintergrundrauschen')} type="range" min="-60" max="-25" step="1" value={noiseThreshold} onChange={e=>{const v=+e.target.value;setNoiseThreshold(v);localStorage.setItem('malvo-noise-threshold',String(v))}} onPointerUp={()=>window.dispatchEvent(new Event('malvo-audio-processing'))} onKeyUp={()=>window.dispatchEvent(new Event('malvo-audio-processing'))}/><output>{noiseThreshold} dB</output></label>}
        <label><input type="checkbox" checked={mutedHint} onChange={e=>{setMutedHint(e.target.checked);localStorage.setItem('malvo-muted-hint',e.target.checked?'1':'0');window.dispatchEvent(new Event('malvo-muted-hint'))}}/><span><b>{tr('Speaking while muted reminder','Hinweis beim Sprechen trotz Mute')}</b><small>{tr('Measures microphone volume locally while muted. Nothing is recorded, sent or played back.','Misst bei Mute die Mikrofonlautstärke lokal. Es wird nichts aufgezeichnet, übertragen oder wiedergegeben.')}</small></span></label>
      </div>
      <p className="fine-print" style={{ marginTop: '16px' }}>
        {tr(
          'Devices apply to your next call. Volume changes apply immediately.',
          'Geräte gelten ab dem nächsten Anruf. Lautstärke wirkt sofort.'
        )}
      </p>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
